/**
 * Org settings -> Billing and plan: which billing notices the signed-in org
 * admin or owner gets for this organization (docs/Decisions.md D54 item 3,
 * D59). Each person chooses for themselves; payment issues are on until
 * changed.
 */
import { useEffect, useState } from 'react';
import * as billingApi from '../api/billing';
import { useOrg } from '../lib/org';
import { useToast } from './toast';
import { ErrorAlert, Spinner } from './ui';
import { cn, ui } from '../lib/ui';

export default function BillingNotificationSettings() {
  const { activeOrg } = useOrg();
  const toast = useToast();
  const [categories, setCategories] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setCategories(null);
    billingApi
      .getBillingSubscriptions()
      .then(d => {
        if (cancelled) return;
        setCategories(d.categories || []);
        setSelected(new Set(d.subscribed || []));
      })
      .catch(err => {
        if (cancelled) return;
        setCategories([]);
        setError(err);
      });
    return () => {
      cancelled = true;
    };
  }, [activeOrg?.orgId]);

  function toggle(id, on) {
    setSelected(current => {
      const next = new Set(current);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }

  async function onSave(event) {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const d = await billingApi.updateBillingSubscriptions([...selected]);
      setSelected(new Set(d.subscribed || []));
      toast.success('Billing notifications saved.');
    } catch (err) {
      setError(err);
      toast.error(err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <form onSubmit={onSave} className={cn(ui.card, 'mb-4')} aria-labelledby="billing-notices-title">
      <h3 id="billing-notices-title" className={ui.h3}>
        Billing notifications
      </h3>
      <ErrorAlert error={error} />
      <p className={cn(ui.muted, 'text-body-s')}>
        Choose what you are notified about for this organization. Your choices are yours alone;
        other admins choose their own.
      </p>
      {categories === null ? <Spinner /> : null}
      {categories && categories.length > 0 ? (
        <fieldset className="mt-2">
          <legend className="sr-only">Billing notifications</legend>
          {categories.map(c => (
            <label key={c.id} className={cn(ui.row, 'text-body-s items-start')}>
              <input
                type="checkbox"
                checked={selected.has(c.id)}
                onChange={e => toggle(c.id, e.target.checked)}
              />{' '}
              <span>
                <strong>{c.label}</strong>
                <span className={cn(ui.hint, 'block mt-0!')}>{c.description}</span>
              </span>
            </label>
          ))}
        </fieldset>
      ) : null}
      <button
        type="submit"
        className={cn(ui.btnPrimary, 'mt-4')}
        disabled={saving || !categories?.length}
      >
        {saving ? 'Saving...' : 'Save notifications'}
      </button>
    </form>
  );
}
